import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router';
import { z } from 'zod';
import { createPartner, listPartners } from '@/api/partners';
import { isApiError } from '@/api/errors';
import type { PartnerStatus, PartnerView } from '@/api/types';
import { useCan } from '@/auth/session';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable } from '@/ui/DataTable';
import { PageHeader } from '@/ui/PageHeader';
import { QueryError } from '@/ui/QueryError';

const STATUS_TONE: Record<PartnerStatus, 'secondary' | 'outline' | 'destructive'> = {
  ACTIVE: 'secondary',
  SUSPENDED: 'outline',
  CLOSED: 'destructive',
};

const columns: ColumnDef<PartnerView, never>[] = [
  {
    accessorKey: 'name',
    header: 'Name',
    cell: ({ row }) => (
      <Link
        to={`/partners/${row.original.id}`}
        className="font-medium text-primary hover:underline"
      >
        {row.original.name}
      </Link>
    ),
  },
  { accessorKey: 'slug', header: 'Slug' },
  { accessorKey: 'type', header: 'Type' },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => (
      <Badge variant={STATUS_TONE[row.original.status]}>{row.original.status}</Badge>
    ),
  },
  {
    accessorKey: 'operating_mode',
    header: 'Mode',
    cell: ({ row }) =>
      row.original.operating_mode === 'OPERATOR' ? (
        <Badge>OPERATOR</Badge>
      ) : (
        <span className="text-xs text-muted-foreground">Delegated</span>
      ),
  },
  {
    accessorKey: 'created_at',
    header: 'Created',
    cell: ({ row }) => new Date(row.original.created_at).toLocaleDateString(),
  },
];

export function PartnersListPage() {
  const can = useCan();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState<PartnerStatus | 'ALL'>('ALL');
  const [page, setPage] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);

  const query = useQuery({
    queryKey: ['partners', { q, status, page }],
    queryFn: () =>
      listPartners({ q: q || undefined, status: status === 'ALL' ? undefined : status, page }),
  });

  return (
    <>
      <PageHeader
        title="Partners"
        actions={
          can('partners.manage') && (
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button>New partner</Button>
              </DialogTrigger>
              <CreatePartnerDialog onCreated={() => setCreateOpen(false)} />
            </Dialog>
          )
        }
      />

      <div className="flex flex-wrap gap-2">
        <Input
          placeholder="Search by name or slug"
          className="max-w-xs"
          value={q}
          onChange={(event) => {
            setPage(0);
            setQ(event.target.value);
          }}
        />
        <Select
          value={status}
          onValueChange={(value) => {
            setPage(0);
            setStatus(value as PartnerStatus | 'ALL');
          }}
        >
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All statuses</SelectItem>
            <SelectItem value="ACTIVE">Active</SelectItem>
            <SelectItem value="SUSPENDED">Suspended</SelectItem>
            <SelectItem value="CLOSED">Closed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {query.isError ? (
        <QueryError error={query.error} onRetry={() => void query.refetch()} />
      ) : (
        <>
          <DataTable
            columns={columns}
            data={query.data?.items ?? []}
            getRowId={(row) => row.id}
            isLoading={query.isPending}
            empty="No partners match those filters."
          />
          {query.data && query.data.total_pages > 1 && (
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>
                Page {query.data.page + 1} of {query.data.total_pages}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 0}
                  onClick={() => setPage((p) => p - 1)}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page + 1 >= query.data.total_pages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
}

const createPartnerSchema = z.object({
  name: z.string().trim().min(1, 'Name is required.').max(200, 'Name is too long.'),
  slug: z
    .string()
    .trim()
    .min(1, 'Slug is required.')
    .max(80, 'Slug is too long.')
    .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, 'Lowercase letters, digits and single hyphens only.'),
});
type CreatePartnerForm = z.infer<typeof createPartnerSchema>;

function CreatePartnerDialog({ onCreated }: { onCreated: () => void }) {
  const queryClient = useQueryClient();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreatePartnerForm>({
    resolver: zodResolver(createPartnerSchema),
    defaultValues: { name: '', slug: '' },
  });

  const create = useMutation({
    mutationFn: createPartner,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['partners'] });
      reset();
      onCreated();
    },
  });

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>New partner</DialogTitle>
      </DialogHeader>

      {create.isError && (
        <Alert variant="destructive">
          <AlertDescription>
            {isApiError(create.error) ? create.error.message : 'Could not create the partner.'}
          </AlertDescription>
        </Alert>
      )}

      <form id="create-partner" onSubmit={handleSubmit((values) => create.mutate(values))}>
        <FieldGroup>
          <Field data-invalid={Boolean(errors.name)}>
            <FieldLabel htmlFor="partner-name">Name</FieldLabel>
            <Input id="partner-name" autoFocus {...register('name')} />
            <FieldError errors={[errors.name]} />
          </Field>
          <Field data-invalid={Boolean(errors.slug)}>
            <FieldLabel htmlFor="partner-slug">Slug</FieldLabel>
            <Input id="partner-slug" placeholder="acme-resellers" {...register('slug')} />
            <FieldError errors={[errors.slug]} />
          </Field>
        </FieldGroup>
      </form>

      <DialogFooter>
        <Button type="submit" form="create-partner" disabled={create.isPending}>
          {create.isPending ? 'Creating…' : 'Create partner'}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
